import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  Gauge, Building2, Layers, Boxes, Megaphone, UserCog, ScrollText,
  LogOut, Menu, X, ShieldAlert, ExternalLink,
} from "lucide-react";
import { usePlatformAuth } from "@/context/PlatformAuthContext";

const NAV = [
  { to: "/platform", label: "Overview", icon: Gauge, end: true, testid: "pnav-overview" },
  { to: "/platform/organizations", label: "Organizations", icon: Building2, testid: "pnav-orgs" },
  { to: "/platform/plans", label: "Subscription Plans", icon: Layers, testid: "pnav-plans" },
  { to: "/platform/modules", label: "Modules", icon: Boxes, testid: "pnav-modules" },
  { to: "/platform/announcements", label: "Announcements", icon: Megaphone, testid: "pnav-announcements" },
  { to: "/platform/admins", label: "Admins", icon: UserCog, testid: "pnav-admins" },
  { to: "/platform/audit", label: "Audit Trail", icon: ScrollText, testid: "pnav-audit" },
];

export default function PlatformShell({ children }) {
  const { admin, logout } = usePlatformAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const signOut = async () => {
    await logout();
    navigate("/platform/login");
  };

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 flex">
      <aside
        data-testid="platform-sidebar"
        className={`fixed z-40 inset-y-0 left-0 w-[248px] bg-[#14121f] text-slate-300 flex flex-col transition-transform duration-200 lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="px-5 py-5 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-heading text-lg font-bold tracking-tight text-white">SyncBooks</div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-violet-400">
                Platform Console
              </div>
            </div>
            <button className="lg:hidden text-slate-400" onClick={() => setOpen(false)} data-testid="platform-sidebar-close">
              <X size={18} />
            </button>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {NAV.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors duration-150 ${
                  isActive ? "bg-violet-600 text-white font-medium" : "hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Icon size={16} strokeWidth={2} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-white/10 p-4 space-y-3">
          <a
            href="/dashboard"
            className="flex items-center gap-2 text-[11px] text-slate-400 hover:text-white transition-colors duration-150"
          >
            <ExternalLink size={12} /> Open tenant app
          </a>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-xs text-slate-200 truncate">{admin?.name}</div>
              <div className="flex items-center gap-1 text-[11px] text-violet-400">
                <ShieldAlert size={11} /> Superadmin
              </div>
            </div>
            <button
              onClick={signOut}
              data-testid="platform-logout"
              className="text-slate-400 hover:text-rose-400 transition-colors duration-150"
              title="Sign out"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-slate-900/50 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1 lg:ml-[248px] min-w-0">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-slate-200 bg-white/95 backdrop-blur px-4 sm:px-6 py-3">
          <button className="lg:hidden text-slate-600" onClick={() => setOpen(true)} data-testid="platform-sidebar-open">
            <Menu size={20} />
          </button>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-700">
              PLATFORM
            </span>
            <span className="hidden sm:inline">
              Changes here affect every tenant on {window.location.host}
            </span>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
